import { Link } from "react-router-dom";
import { ShieldCheck, HeartPulse, Landmark } from "lucide-react";

const BONFIRE_IMG = "/images/campfire.webp";
const FISHING_IMG = "/images/fishing.webp";
const TREK_IMG = "/images/trek.webp";
const DAVID_IMG = "/images/cattle-photo.webp";

export const StatsStrip = () => (
  <section className="border-y border-hearth-line bg-hearth-surface paper" data-testid="authority-strip">
    <div className="max-w-7xl mx-auto px-6 py-14 text-center">
      <p className="font-display text-3xl sm:text-4xl lg:text-5xl text-hearth-ink leading-snug" data-testid="authority-statement">
        40 years. Thousands of sessions. <span className="gilded">One rule: No&nbsp;BS.</span>
      </p>
    </div>
  </section>
);

export const Marriage = () => (
  <section className="py-28 relative grain" data-testid="marriage-section">
    <div className="max-w-7xl mx-auto px-6">
      <div>
        <p className="eyebrow mb-6">Marriage — His Life's Greatest Passion</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-hearth-ink leading-tight" data-testid="marriage-headline">
          He got him to come.<br />
          <span className="gilded">That's the Matt effect.</span>
        </h2>
        <p className="mt-8 text-hearth-muted text-lg leading-relaxed max-w-xl">
          Every partner who has ever tried and failed to get their person into counselling knows how
          extraordinary that sentence is. Blokes walk into a session with Matt willingly — because
          there's no couch, no clipboard, and no chance of being made to feel defective.
        </p>
        <p className="mt-5 text-hearth-muted text-lg leading-relaxed max-w-xl">
          Just two people learning to hear each other again, with forty years of marriage in the room.
          Karen can sit in — just ask.
        </p>
        <Link
          to="/book?type=couples_session"
          data-testid="marriage-book-button"
          className="mt-10 inline-block bg-hearth-ember hover:bg-hearth-ochre hover:text-hearth-bg text-hearth-ink font-body font-semibold text-sm tracking-widest uppercase px-8 py-4 transition-colors"
        >
          Book a Couples Session
        </Link>
      </div>
    </div>
  </section>
);

export const FrescoBand = () => (
  <section className="relative h-[70vh] overflow-hidden border-y border-hearth-line" data-testid="fresco-band">
    <img src="/images/fresco.webp" alt="Fresco — two figures reaching toward each other through light and shadow" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-b from-hearth-bg via-transparent to-hearth-bg" />
    <div className="relative h-full flex items-end justify-center pb-16 px-6">
      <p className="font-essay italic text-3xl sm:text-4xl lg:text-5xl text-hearth-ink text-center max-w-4xl leading-snug" data-testid="fresco-quote">
        "Trauma is a chapter, not the title."
      </p>
    </div>
  </section>
);

const REALMS = [
  { title: "Men who've never said it out loud", body: "Somewhere safe to be honest without being mocked, managed, or made to feel defective. Saying it is the courage. He brings the quiet." },
  { title: "Couples who became strangers", body: "Two people who managed to love each other and misunderstand each other at the same time. Both heard. Both matter. Karen can sit in too, if you want her." },
  { title: "Kids who carried too much, too young", body: "A school chaplain's patience and a grandfather's gentleness. Blue Card held, first aid certified, and very hard to shock." },
  { title: "Families finding their way back", body: "Messy is fine. He has raised six and hosted a rotating cast of adopted strays at his own table. There is room for yours." },
];

export const Realms = () => (
  <section className="py-24 relative grain" data-testid="realms-section">
    <div className="max-w-7xl mx-auto px-6">
      <p className="eyebrow mb-4">Who Sits Beside the Fire</p>
      <h2 className="font-display font-bold text-4xl sm:text-5xl text-hearth-ink max-w-2xl leading-tight">
        Every realm of a life is welcome here.
      </h2>
      <p className="mt-6 text-hearth-muted max-w-xl leading-relaxed">
        Physical, emotional, psychological, spiritual — whatever is sitting on you, he has heard a version of it before and he isn't fazed.
      </p>
      <div className="mt-16 grid md:grid-cols-2 gap-px bg-hearth-line border border-hearth-line">
        {REALMS.map((r) => (
          <div key={r.title} className="bg-hearth-bg p-10 group hover:bg-hearth-surface transition-colors" data-testid={`realm-${r.title.slice(0, 12).toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <h3 className="font-display font-bold text-2xl text-hearth-ink group-hover:text-hearth-ochre transition-colors">{r.title}</h3>
            <p className="mt-4 text-hearth-muted leading-relaxed text-sm">{r.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const SETTINGS_CARDS = [
  { img: FISHING_IMG, title: "On the water", body: "A rod, a river, and no clock-watching." },
  { img: TREK_IMG, title: "On the track", body: "Weekend treks where the talking walks with you." },
  { img: BONFIRE_IMG, title: "Beside the fire", body: "Camps, backyards, and fire under the stars." },
];

export const SessionSettings = () => (
  <section id="sessions" className="py-24 bg-hearth-surface border-y border-hearth-line" data-testid="settings-section">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid lg:grid-cols-2 gap-16 items-start">
        <div>
          <p className="eyebrow mb-4">Where It Happens</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl text-hearth-ink leading-tight">
            The room is wherever you can breathe.
          </h2>
          <p className="mt-6 text-hearth-muted leading-relaxed max-w-md">
            Clinic, kitchen, coffee or a fishing line — you pick.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {SETTINGS_CARDS.map((c, i) => (
            <div key={c.title} className={`relative overflow-hidden border border-hearth-line ${i === 0 ? "col-span-2" : ""}`} data-testid={`setting-card-${i}`}>
              <img src={c.img} alt={c.title} className={`w-full object-cover grayscale-[30%] hover:grayscale-0 transition-all duration-700 ${i === 0 ? "h-56" : "h-64"}`} />
              <div className="absolute inset-0 bg-gradient-to-t from-hearth-bg via-transparent to-transparent" />
              <div className="absolute bottom-0 p-5">
                <p className="font-display font-bold text-lg text-hearth-ink">{c.title}</p>
                <p className="text-hearth-muted text-xs mt-1">{c.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const PRICES = [
  { id: "first_consult", name: "First Consult", price: "$225", note: "Ninety minutes. The whole story, start to wherever it lands." },
  { id: "ongoing_single", name: "Ongoing Session", price: "$200", note: "One bloke, one hour, no small print." },
  { id: "couples_session", name: "Couples Session", price: "$250", note: "Both heard. Karen available on request." },
  { id: "family_session", name: "Family Session", price: "Let's talk", note: "Every family is a different shape. Price agreed together, before anything starts." },
  { id: "gift", name: "Gift a Session", price: "From $200", note: "For the bloke who'd never book himself. You pay, he gets a code.", href: "/gift" },
];

export const Pricing = () => (
  <section className="py-24" data-testid="pricing-section">
    <div className="max-w-7xl mx-auto px-6">
      <p className="eyebrow mb-4">The Tariff, On the Table</p>
      <h2 className="font-display font-bold text-4xl sm:text-5xl text-hearth-ink">No hidden anything.</h2>
      <div className="mt-16 border-t border-hearth-line">
        {PRICES.map((p) => (          <div key={p.id} className="group grid md:grid-cols-[1fr_auto_auto] gap-6 items-center py-8 border-b border-hearth-line hover:bg-hearth-surface transition-colors px-4" data-testid={`price-row-${p.id}`}>
            <div>
              <h3 className="font-display font-bold text-2xl text-hearth-ink">{p.name}</h3>
              <p className="text-hearth-muted text-sm mt-2">{p.note}</p>
            </div>
            <p className="font-display font-bold text-3xl text-hearth-ochre md:text-right">{p.price}</p>
            <Link
              to={p.href || `/book?type=${p.id}`}
              data-testid={`price-book-${p.id}`}
              className="justify-self-start md:justify-self-end border border-hearth-line group-hover:border-hearth-ember group-hover:bg-hearth-ember text-hearth-muted group-hover:text-hearth-ink text-xs font-semibold uppercase tracking-widest px-6 py-3 transition-colors"
            >
              Book This
            </Link>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const MerchBand = () => (
  <section className="py-20 border-y border-hearth-line bg-hearth-surface paper" data-testid="merch-band">
    <div className="max-w-7xl mx-auto px-6 flex flex-col items-center text-center gap-8">
      <img src="/images/logo-mark.png" alt="The hat, the beard, the halo" className="w-52 h-auto" data-testid="merch-band-logo" />
      <div>
        <p className="eyebrow mb-3">The Merch</p>
        <h2 className="font-display text-4xl sm:text-5xl text-hearth-ink">Wear the hat.</h2>
        <p className="mt-3 text-hearth-muted leading-relaxed max-w-lg mx-auto">
          Tees, caps, hoodies, pins, totes, stickers, stubby holders — the mark on all of it.
        </p>
      </div>
      <Link to="/merch" data-testid="merch-band-link"
        className="bg-hearth-ember hover:bg-hearth-ochre hover:text-hearth-bg text-hearth-ink font-body font-semibold text-sm tracking-widest uppercase px-8 py-4 transition-colors">
        Browse the Merch
      </Link>
    </div>
  </section>
);

const CREDS = [
  { icon: Landmark, title: "40 Years in Practice", body: "Counsellor, school chaplain, theologian, farmer." },
  { icon: ShieldCheck, title: "Working with Children", body: "Blue Card held and current." },
  { icon: HeartPulse, title: "First Aid Certified", body: "Because some sessions happen far from a clinic." },
];

export const Credentials = () => (
  <section className="py-24 bg-hearth-surface border-y border-hearth-line relative grain" data-testid="credentials-section">
    <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
      <div className="deco-frame order-2 lg:order-1">
        <img src={DAVID_IMG} alt="Matt on his land — hat, plaid, boots, watching the mob at golden hour" className="w-full h-[480px] object-cover" data-testid="credentials-image" />
      </div>
      <div className="order-1 lg:order-2">
        <p className="eyebrow mb-4">The Credentials, Such As They Are</p>
        <h2 className="font-display font-bold text-4xl sm:text-5xl text-hearth-ink leading-tight">Paper where it matters. Presence everywhere else.</h2>
        <div className="mt-12 space-y-8">
          {CREDS.map((c) => (
            <div key={c.title} className="flex gap-6 items-start border border-hearth-line p-6 hover:border-hearth-ochre transition-colors" data-testid={`credential-${c.title.slice(0, 10).toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <c.icon className="w-8 h-8 text-hearth-ember shrink-0 mt-1" strokeWidth={1.5} />
              <div>
                <h3 className="font-display font-bold text-xl text-hearth-ink">{c.title}</h3>
                <p className="text-hearth-muted text-sm mt-1">{c.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const Privacy = () => (
  <section id="privacy" className="py-24" data-testid="privacy-section">
    <div className="max-w-4xl mx-auto px-6 text-left">
      <p className="eyebrow mb-4">The Pact</p>
      <h2 className="font-display font-bold text-4xl sm:text-5xl text-hearth-ink leading-tight">
        What you tell him stays told.
      </h2>
      <div className="mt-10 space-y-6 font-essay text-xl text-hearth-ink/90 leading-loose">
        <p>
          A priest is covered by law in the confessional. Matt works to the same standard by choice:
          your story is yours, and it stays in the room — or on the river, or beside the fire.
        </p>
        <p>
          This site is built the same way. No advertising trackers. No data sold. No third parties
          reading along. The only things stored are what's needed to book you in and what you
          choose to leave on the wall — and the wall takes no names.
        </p>
        <p className="text-hearth-muted text-lg">
          The one line that never moves: nothing here breaks the law, and nothing here hides from it.
          If someone is in danger of being hurt, the law of the land still applies — that's not red tape, that's decency.
        </p>
      </div>
    </div>
  </section>
);
